import React from 'react';

const RoutingBreakdown = ({ routing, routingGeos = [], routingConfig, unrouted = [] }) => {
  if ((!routing || routing.length === 0) && unrouted.length === 0) return null;

  const total = routing.reduce((sum, r) => sum + r.sent + r.dropped, 0);
  const backupSent = routing
    .filter((r) => r.backup)
    .reduce((sum, r) => sum + r.sent, 0);
  const totalErrors = routing.reduce((sum, r) => sum + r.errors, 0);

  return (
    <div className='bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden'>
      <div className='px-6 py-4 border-b border-slate-200'>
        <h2 className='text-lg font-semibold text-slate-900'>Delivery routing</h2>
        <p className='mt-1 text-sm text-slate-500'>
          Which Sender ID bindings actually carried this campaign.
        </p>

        {routingConfig && routingConfig.length > 0 && (
          <div className='mt-3 space-y-1'>
            {routingGeos.map((geo) => {
              const forGeo = routingConfig.filter((c) => c.geo === geo);
              return (
                <p key={geo} className='text-xs text-slate-500'>
                  <span className='font-medium text-slate-600'>Configured for {geo}:</span>{' '}
                  {forGeo.length
                    ? forGeo
                        .map((c) => `${c.sender_id} (p${c.priority}, ${c.provider_name})`)
                        .join(' → ')
                    : 'no Sender ID binding'}
                </p>
              );
            })}
          </div>
        )}
      </div>

      {unrouted.length > 0 && (
        <div className='px-6 py-3 bg-red-50 border-b border-red-200 text-sm text-red-800'>
          No Sender ID binding for{' '}
          {unrouted.map((r) => `${r.geo || 'unknown GEO'} (${r.count})`).join(', ')} — those
          recipients were never sent. Add a binding on the Sender IDs page and resend.
        </div>
      )}

      {backupSent > 0 && (
        <div className='px-6 py-3 bg-amber-50 border-b border-amber-200 text-sm text-amber-800'>
          The primary gateway returned {totalErrors} error{totalErrors === 1 ? '' : 's'} —{' '}
          {backupSent} of {total} messages were delivered by a backup binding.
        </div>
      )}

      <div className='overflow-x-auto'>
        <table className='w-full text-sm'>
          <thead className='bg-slate-50/50 text-slate-500'>
            <tr>
              <th className='text-left text-xs font-medium uppercase px-6 py-3'>Sender ID</th>
              <th className='text-left text-xs font-medium uppercase px-6 py-3'>GEO</th>
              <th className='text-left text-xs font-medium uppercase px-6 py-3'>Provider</th>
              <th className='text-left text-xs font-medium uppercase px-6 py-3'>Priority</th>
              <th className='text-right text-xs font-medium uppercase px-6 py-3'>Sent</th>
              <th
                className='text-right text-xs font-medium uppercase px-6 py-3'
                title='Gateway errors on this binding. A message that failed here and was delivered by a backup is counted once.'
              >
                Errors
              </th>
              <th
                className='text-right text-xs font-medium uppercase px-6 py-3'
                title='Messages that exhausted every backup and were never delivered.'
              >
                Dropped
              </th>
              <th className='text-right text-xs font-medium uppercase px-6 py-3'>Share</th>
            </tr>
          </thead>
          <tbody className='divide-y divide-slate-100'>
            {routing.map((row) => {
              const handled = row.sent + row.dropped;
              return (
                <tr key={`${row.geo}-${row.sender_id}-${row.priority}`}>
                  <td className='px-6 py-3 font-medium text-slate-900 whitespace-nowrap'>
                    {row.sender_id}
                    {row.backup && (
                      <span className='ml-2 text-xs px-2 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200'>
                        backup
                      </span>
                    )}
                  </td>
                  <td className='px-6 py-3 text-slate-600 whitespace-nowrap'>{row.geo}</td>
                  <td className='px-6 py-3 text-slate-600 whitespace-nowrap'>
                    {row.provider_name}
                  </td>
                  <td className='px-6 py-3 text-slate-600'>{row.priority}</td>
                  <td className='px-6 py-3 text-right text-slate-900'>{row.sent}</td>
                  <td
                    className={`px-6 py-3 text-right ${
                      row.errors ? 'text-amber-600 font-medium' : 'text-slate-400'
                    }`}
                  >
                    {row.errors}
                  </td>
                  <td
                    className={`px-6 py-3 text-right ${
                      row.dropped ? 'text-red-600 font-medium' : 'text-slate-400'
                    }`}
                  >
                    {row.dropped}
                  </td>
                  <td className='px-6 py-3 text-right text-slate-500'>
                    {total ? `${Math.round((handled / total) * 100)}%` : '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default RoutingBreakdown;
